import {
  LitElement,
  html,
  css,
  unsafeHTML,
  utils,
  paths,
  t,
  broadcastReceiver,
  classMap,
} from '@hh/global.js'

if (!customElements.get('hh-modal')) {
  class HHModal extends LitElement {
    static properties = {
      id: {},
      open: {
        reflect: true,
      },
      hideClose: {
        type: Boolean,
        attribute: 'hide-close',
      },
      showBackdrop: {
        type: Boolean,
        attribute: 'show-backdrop',
      },
      isCartNotification: {
        type: Boolean,
        attribute: 'is-cart-notification',
      },
    }

    constructor() {
      super()
      this.closeButtons = this.querySelectorAll('.js-modal-close')
      this.closeButtons.forEach((button) => {
        button.addEventListener('click', this.close.bind(this))
      })

      this.focusAnchor = null

      this.handleFocusAnchorClick = e => {        
        if (!e.target.classList.contains('js-modal-opener')) return
        this.focusAnchor = e.target        
      }
      
      document.addEventListener('click', this.handleFocusAnchorClick)
    }

    focusableSelector =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

    isFocusable(el) {
      return (
        !el.hasAttribute('disabled') &&
        el.offsetParent !== null &&
        getComputedStyle(el).visibility !== 'hidden'
      )
    }

    requestUpdate(name, oldValue) {
      if (name === 'open' && !!this[name]) {
        utils.pauseAllMedia()

        setTimeout(() => {
          this.setInitialFocus()
        }, 0)
      }

      return super.requestUpdate(name, oldValue)
    }

    connectedCallback() {
      super.connectedCallback()

      this.receiver = broadcastReceiver('modal', (...args) => {
        this.openFromDocument(...args)
      })

      window.addEventListener('close', this.closeFromEvent.bind(this))      

      // the cart notification modal was causing a CLS on first load
      // by adding `display: none` to the modal to start, and then
      // removing it when the page is ready, this reduces CLS
      const docIsReady = setInterval(() => {
        if (document.readyState !== 'complete') {
          return
        } else {
          clearInterval(docIsReady)
          this.classList.remove('hidden')
        }
      }, 100)
    }

    disconnectedCallback() {
      super.disconnectedCallback()
      document.removeEventListener('click', this.handleFocusAnchorClick)

      this.receiver?.disconnect()
    }

    setInitialFocus() {
      if (!this.open) return
    
      const dialog = this.shadowRoot.querySelector('.dialog')
      if (!dialog) return
          
      const explicit = this.querySelector('[data-initial-focus]')
      if (explicit && this.isFocusable(explicit)) {
        explicit.focus()
        return
      }
          
      const slot = this.shadowRoot.querySelector('slot')
      const assigned = slot?.assignedElements({ flatten: true }) ?? []
    
      const focusable = assigned
        .flatMap(el =>
          el.matches?.(this.focusableSelector)
            ? [el]
            : Array.from(el.querySelectorAll?.(this.focusableSelector) || [])
        )
        .find(el => this.isFocusable(el))
    
      if (focusable) {
        focusable.focus()
        return
      }
          
      dialog.focus()
    }

    restoreFocus() {            
      if (
        this.focusAnchor &&
        document.contains(this.focusAnchor) &&
        !this.focusAnchor.hasAttribute('disabled')
      ) {                
        this.focusAnchor.focus()
        return
      }
    }   
    resumePdpVideo() {
      const pdpVideo = document.querySelector(
        '.js-product-first-video video, .product__first-video video'
      )
    
      if (!pdpVideo) return
          
      const hasAutoplay = pdpVideo.hasAttribute('autoplay')
    
      if (!hasAutoplay) return
    
      try {
        pdpVideo.play()
      } catch (e) {
        console.warn('Could not resume PDP video:', e)
      }
    }    

    openFromDocument(data) {
      if (this.id === data.id) {
        this.open = true

        if (this.isCartNotification) {
          setTimeout(() => {
            this.open = false
            document.querySelector('html').classList.remove('overflow-hidden')
          }, 3000)
        }
      }

      if (data.element) {
        setTimeout(() => {
          this.querySelector(`[id="${data.element}"]`)?.scrollIntoView()
        }, 0)
      }

      if (!this.isCartNotification) {
        document.querySelector('html').classList.add('overflow-hidden')
      }
    }

    close(event) {      
      document.querySelector('html').classList.remove('overflow-hidden')
      this.restoreFocus()
      if (event.type === 'click') {
        this.open = false
        this.resumePdpVideo()
        return
      }
      event.code.toUpperCase() === 'ESCAPE' && (this.open = false)
    }

    closeFromEvent(event) {
      this.restoreFocus()
      if (event.detail.key === this.id) {
        document.querySelector('html').classList.remove('overflow-hidden')
        this.open = false
      }
    }

    render() {
      return html`<div
      @keyup="${this.close}"
      role="dialog"
      aria-modal="true"
      class="dialog"
      tabindex="-1"
    >
      <button
        @click="${this.close}"
        class="close button ${classMap({
        hide: this.hideClose,
      })}"
        type="button"
        aria-label="${t('accessibility.close')}"
      >
        ${unsafeHTML(paths.close)}
      </button>
      <div
        class="content"
        role="document"
        aria-label="${t('products.modal.label')}"
        tabindex="0"
      >        
        <slot></slot>        
      </div>
      ${this.showBackdrop
          ? html`<div class="backdrop" @click="${this.close}"></div>`
          : ''}
    </div>`
    }

    static styles = css`
    :host {
      background-color: var(--hh-background);
      box-sizing: border-box;
      height: 100%;
      left: 0;
      opacity: 0;
      position: fixed;
      top: 0;
      width: 100%;
      visibility: hidden;
      z-index: -1;
    }

    :host([size='flyout']) {
      max-width: var(--hh-max-width, 400px);
      height: auto;
      top: var(--hh-flyout-offset, 30px);
      right: var(--hh-flyout-offset, 30px);
      left: revert;
      border: 0.0625rem solid var(--hh-border-color);
    }

    :host([open='true']) {
      opacity: 1;
      visibility: visible;
      z-index: 1000;
    }

    .button {
      width: 2.5000rem;
      position: fixed;
      top: 2.5000rem;
      right: 2.5000rem;
      display: flex;
      background-color: var(--hh-background);
      border: 0.0625rem solid var(--hh-border-color);
      border-radius: 50%;
      color: var(--hh-color);
      align-items: center;
      justify-content: center;
      cursor: pointer;
      padding: 0.7500rem;
      z-index: 2;
    }

    :host([size='flyout']) .button {
      position: absolute;
      top: var(--hh-close-top, 10px);
      right: var(--hh-close-right, 10px);
    }

    .content {
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: auto;
      width: 100%;
      padding: var(--hh-padding);
      position: relative;
      box-sizing: border-box;
    }

    :host([size='tooltip']),
    :host([size='auto']) {
      background-color: transparent;
    }

    :host([size='tooltip']) .content,
    :host([size='flyout']) .content,
    :host([size='auto']) .content {
      height: auto;
    }

    :host([size='tooltip']) .dialog,
    :host([size='auto']) .dialog {
      height: 100%;
      display: flex;
    }

    :host([size='tooltip']) .content,
    :host([size='auto']) .content {
      margin: auto;
      background-color: var(--hh-background);
      z-index: 1;
    }

    :host([size='tooltip']) .content {
      max-width: 540px;
      width: 72%;
    }

    :host([size='auto']) .content {
      width: auto;
    }

    ::slotted(*) {
      width: var(--hh-slotted-width, auto);
      height: var(--hh-slotted-height, auto);
      margin: var(--hh-slotted-margin);
    }

    .hide {
      display: none;
    }

    .backdrop {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.75);
      backdrop-filter: blur(2px);
    }

    .button.close {
      z-index: 3;
    }
  `
  }

  customElements.define('hh-modal', HHModal)
}
